import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const ContactModuleEditor = ({ data, onChange }) => {
  const contacts = data.contacts || [];
  
  const addContact = () => {
    onChange({
      ...data,
      contacts: [...contacts, {
        id: Date.now().toString(),
        title: 'New Contact',
        name: '',
        company: '',
        email: '',
        phone: '',
        address: ''
      }]
    });
  };
  
  const updateContact = (idx, field, value) => {
    const updated = [...contacts];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...data, contacts: updated });
  };
  
  const removeContact = (idx) => {
    onChange({ ...data, contacts: contacts.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-6">
      {/* Form Settings */}
      <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50">
        <h4 className="font-medium text-white mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-orange-400" />
          Contact Form Settings
        </h4>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch 
              checked={data.show_form !== false}
              onCheckedChange={(checked) => onChange({...data, show_form: checked})}
            />
            <Label>Show contact form on website</Label>
          </div>
          
          {data.show_form !== false && (
            <>
              <div className="space-y-2">
                <Label>Form Submissions Email</Label>
                <Input 
                  type="email"
                  value={data.form_email || ''} 
                  onChange={(e) => onChange({...data, form_email: e.target.value})}
                  placeholder="Where should enquiries be sent?"
                />
                <p className="text-xs text-slate-400">Form submissions will be emailed to this address</p>
              </div>
              
              <div className="space-y-2">
                <Label>Form Message</Label>
                <Input 
                  value={data.form_message || ''} 
                  onChange={(e) => onChange({...data, form_message: e.target.value})}
                  placeholder="Get in touch for bookings and enquiries"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Contact Sets */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base">Contact Details ({contacts.length})</Label>
          <Button variant="outline" size="sm" onClick={addContact}>
            <Plus className="w-4 h-4 mr-1" /> Add Contact
          </Button>
        </div>
        
        <p className="text-sm text-slate-400">
          Add multiple contacts for different regions or purposes (e.g., UK Agent, US Agent, General Enquiries)
        </p>
        
        {contacts.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <Phone className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No contact details yet. Add your first contact!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact, idx) => (
              <div key={contact.id || idx} className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <Label>Contact Title</Label>
                    <Input 
                      value={contact.title || ''} 
                      onChange={(e) => updateContact(idx, 'title', e.target.value)}
                      placeholder="e.g., UK Agent, US Management, General Enquiries"
                      className="font-medium"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeContact(idx)} 
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20 ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input 
                      value={contact.name || ''} 
                      onChange={(e) => updateContact(idx, 'name', e.target.value)}
                      placeholder="Contact person name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input 
                      value={contact.company || ''} 
                      onChange={(e) => updateContact(idx, 'company', e.target.value)}
                      placeholder="Company or agency name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                      type="email"
                      value={contact.email || ''} 
                      onChange={(e) => updateContact(idx, 'email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input 
                      value={contact.phone || ''} 
                      onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                      placeholder="+44 20 1234 5678"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Address (Optional)</Label>
                  <Textarea 
                    value={contact.address || ''} 
                    onChange={(e) => updateContact(idx, 'address', e.target.value)}
                    placeholder="Full address..."
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

